import { Link } from 'react-router-dom';
import ClearPathLogo from './ClearPathLogo';

interface Section {
  id: string;
  heading: string;
  body: string[];
}

interface LegalDoc {
  title: string;
  effective: string;
  intro: string;
  sections: Section[];
}

/**
 * Shared layout for /terms and /privacy — a clean, readable legal page in the
 * ClearPath design system, with a table-of-contents jump list.
 */
export default function LegalPage({ doc }: { doc: LegalDoc }) {
  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Minimal header */}
      <header className="border-b border-[#E6E2DE] bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" aria-label="ClearPath home">
            <ClearPathLogo variant="dark" size="sm" />
          </Link>
          <Link to="/" className="font-sans text-sm text-[#6B6670] hover:text-[#4A2D5E] transition-colors">
            ← Back to ClearPath
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-serif text-3xl md:text-4xl text-[#2D2A2E] mb-1">{doc.title}</h1>
        <p className="font-sans text-sm text-[#6B6670] mb-8">{doc.effective}</p>

        <p className="font-sans text-[15px] leading-relaxed text-[#2D2A2E] mb-8">{doc.intro}</p>

        {/* Table of contents */}
        <nav className="bg-white border border-[#E6E2DE] rounded-[14px] p-5 mb-10">
          <div className="font-sans text-xs font-semibold uppercase tracking-widest text-[#6B6670] mb-3">
            Contents
          </div>
          <ol className="space-y-1.5">
            {doc.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="font-sans text-sm text-[#4A2D5E] hover:underline">
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="space-y-10">
          {doc.sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-serif text-xl text-[#2D2A2E] mb-3">{s.heading}</h2>
              <div className="space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="font-sans text-[15px] leading-relaxed text-[#2D2A2E]">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-14 pt-6 border-t border-[#E6E2DE] font-sans text-sm text-[#6B6670] flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/terms" className="hover:text-[#4A2D5E]">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-[#4A2D5E]">Privacy Policy</Link>
          <a href="mailto:tryclearpath@gmail.com" className="hover:text-[#4A2D5E]">Contact</a>
          <span className="text-[#9B95A0]">© 2026 ClearPath</span>
        </footer>
      </main>
    </div>
  );
}
